/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from 'vue'
import VueRouter from 'vue-router'
import LoginService from '@/services/api/LoginService'
import auth from '@/auth/authService'
import store from '@/store/store'
import { isUserLoggedIn } from '@/util/Util'
import { page, time } from 'vue-analytics'
import NotificationService from '../services/api/NotificationService'
import apps from './routes/apps'
import pages from './routes/pages'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'hash',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'Mural',
      component: () => import('@/views/pages/Home.vue'),
      meta: {},
    },
    ...apps,
    ...pages,
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

router.afterEach((to) => {
  if (to.path !== '/login' && to.path !== '/a2fa') {
    try {
      require('@/services/SessionTimeoutService').default.onUserNavigation()
    } catch (error) {
      // Serviço pode não estar pronto no primeiro boot.
    }
  }

  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }

  // Evita overlay de loading preso ao sair do Mural (ou de qualquer tela com $vs.loading)
  if (router.app && router.app.$vs && router.app.$vs.loading) {
    router.app.$vs.loading.close()
  }

  // Remove classe legada do layout externo quando não está no Mural
  if (to.name !== 'Mural') {
    document.querySelectorAll('.app-content.mural-page-background').forEach(el => {
      el.classList.remove('mural-page-background')
    })
  }
})

const DEFAULT_TITLE = `${process.env.VUE_APP_NAME}`;
router.beforeEach((to, from, next) => {
  store.commit('common/DISABLED_CONTEXT_MENU', false)

  const frozenRouter = store.getters['frozenRouter']

  if (frozenRouter) {
    console.log('router is currently frozen')
    next(false)
    return
  }

  let acl = Vue.prototype.$acl
  const profile = store.getters.activeProfile
  const userInfo = localStorage.getItem('userInfo')

  // Check if user has permission to access the route
  if (userInfo !== null) {
    const isNotPublicisPublic = to.meta.rule && to.meta.rule !== 'public'
    if (!acl.hasAnyPermission(to.meta.rule) && isNotPublicisPublic) {
      // next('/not-authorized')
    }

    // Set title
    document.title = to.meta.pageTitle ? DEFAULT_TITLE + ' - ' + to.meta.pageTitle : DEFAULT_TITLE
  }

  // Set last url path
  let appInfoStr = localStorage.getItem('appInfo')
  let appInfo = appInfoStr ? JSON.parse(appInfoStr) : {}
  if (
    to.path !== '/login' &&
    to.name !== 'error-404' &&
    to.name !== 'reset-password' &&
    to.name !== 'not-authorized' &&
    to.name !== 'invalid-signature' &&
    to.name !== 'forgot-password' &&
    to.name !== 'social_register' &&
    to.name !== 'verified' &&
    to.name !== 'register'
  ) {
    if (!appInfo) {
      appInfo = {
        lastUrlPath: to.path
      }
    } else {
      appInfo.lastUrlPath = to.path
    }
    localStorage.setItem('appInfo', JSON.stringify(appInfo))
  }

  if (
    to.path !== '/login' &&
    to.name !== 'error-404' &&
    to.name !== 'forgot-password' &&
    to.name !== 'reset-password' &&
    to.name !== 'not-authorized' &&
    to.name !== 'invalid-signature' &&
    to.name !== 'verified' &&
    to.name !== 'register' &&
    to.name !== 'restricted_user' &&
    to.name !== 'first-access' &&
    to.name !== 'form-lgpd' &&
    to.name !== 'form-incident' &&
    to.name !== 'form-due-diligence' &&
    to.name !== 'systematic-intimidation' &&
    to.name !== 'reporting-channel-form' &&
    to.name !== 'confirm-schedule' &&
    to.name !== 'document-public' &&
    to.name !== 'form-protocol-query' &&
    to.name !== 'newsletter-public' &&
    to.name !== 'maintenance' &&
    to.name !== 'a2fa' &&
    to.name !== 'drive-public' &&
    to.name !== 'portal-cebas-credentialing-public' &&
    to.name !== 'portal-cebas-vacancies-public' &&
    from.name !== 'first-access' &&
    from.name !== 'login'
  ) {
    let service = LoginService.build()
    let notificationService = NotificationService.build(router.app)
    service.post('v1/logged', {}).then(
      data => {
        router.app.$store.dispatch('resetUnreadNotifications', notificationService.constructManyNotifications(data.notifications))
        router.app.$store.dispatch('setPingInterval', data.ping_interval)
        if ('slow_kbps_rate' in data) router.app.$store.dispatch('setSlowKbpsRate', data.slow_kbps_rate)
        router.app.$store.dispatch('updateUserInfo', {email_verified_at: data.email_verified_at})
        Vue.prototype.$acl.setPermissions(data.permissions)

        const user = JSON.parse(userInfo)
        if (to.name !== 'complement' && user && 'uncomplete_legal_entity_id' in user && user.uncomplete_legal_entity_id > 0) {
          next(`/complement/${user.uncomplete_legal_entity_id}`)
          return
        }

        next()
      },
      error => {
        let app = router.app
        const sessionTimeoutService = require('@/services/SessionTimeoutService').default
        if (sessionTimeoutService.isExpiredPromptActive()) {
          next(false)
          return
        }
        if(from.name !== null) {
          Vue.prototype.notifyError(app.$vs, 'Usuário não logado.')
          next('/login')
          return
        }
        const errorObject = JSON.parse(error.data)
        if (error.status !== 401) {
          router.app.$store.dispatch('auth/logout', {vs: app.$vs})
        }
        if (error.status === 401 && errorObject.error === 'unauthenticated2fa') {

        }
      }
    )
  } else {
    next()
  }


  if (to.path === '/login' && isUserLoggedIn()) {
    const sessionTimeoutService = require('@/services/SessionTimeoutService').default
    if (sessionTimeoutService.isRedirectingToLogin()) {
      return
    }
    if (!Vue.prototype.isEmpty(store.getters.userToken)) {
      let app = router.app
      app.notifyWarning(app.$vs, 'Você já está autenticado.', 3000)
    }
    next('/')
    return
  }
})

export default router
